import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar, AlertCircle, Pencil } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Bills({ user, onLogout }) {
  const [bills, setBills] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    due_date: '',
    recurring: false,
    frequency: 'monthly'
  });

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bills`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBills(response.data);
    } catch (error) {
      toast.error('Failed to fetch bills');
    }
  };

  const handleAddNew = () => {
    setEditMode(false);
    setEditingId(null);
    setFormData({
      name: '',
      amount: '',
      due_date: '',
      recurring: false,
      frequency: 'monthly'
    });
    setOpen(true);
  };

  const handleEdit = (bill) => {
    setEditMode(true);
    setEditingId(bill.id);
    setFormData({
      name: bill.name,
      amount: bill.amount.toString(),
      due_date: bill.due_date,
      recurring: bill.recurring,
      frequency: bill.frequency || 'monthly'
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const billData = {
        ...formData,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
        frequency: formData.recurring ? formData.frequency : null
      };

      if (editMode) {
        await axios.put(
          `${API}/bills/${editingId}`,
          billData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Bill updated successfully!');
      } else {
        await axios.post(
          `${API}/bills`,
          billData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Bill added successfully!');
      }

      setOpen(false);
      setFormData({
        name: '',
        amount: '',
        due_date: '',
        recurring: false,
        frequency: 'monthly'
      });
      setEditMode(false);
      setEditingId(null);
      fetchBills();
    } catch (error) {
      toast.error(editMode ? 'Failed to update bill' : 'Failed to add bill');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bills/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bill deleted!');
      fetchBills();
    } catch (error) {
      toast.error('Failed to delete bill');
    }
  };

  const isUpcoming = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="bills-page">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/bills" />

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2" data-testid="bills-title">
              Bills & Reminders
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Manage your recurring payments</p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={handleAddNew}
                className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-full"
                data-testid="add-bill-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Bill
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800" data-testid="bill-dialog">
              <DialogHeader>
                <DialogTitle className="dark:text-gray-100">
                  {editMode ? 'Edit Bill' : 'Add New Bill'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Bill Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Electricity, Netflix"
                    required
                    data-testid="name-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="amount-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                    data-testid="due-date-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    id="recurring-switch"
                    role="switch"
                    aria-checked={formData.recurring}
                    onClick={() => {
                      setFormData({ ...formData, recurring: !formData.recurring });
                    }}
                    data-testid="recurring-switch"
                    className={`
                      relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2
                      ${formData.recurring ? 'bg-rose-600' : 'bg-gray-400 dark:bg-gray-600'}
                    `}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 
                        transition duration-200 ease-in-out
                        ${formData.recurring ? 'translate-x-4' : 'translate-x-0'}
                      `}
                    />
                  </button>
                  <Label 
                    htmlFor="recurring-switch" 
                    className="dark:text-gray-300 cursor-pointer"
                    onClick={() => {
                      setFormData({ ...formData, recurring: !formData.recurring });
                    }}
                  >
                    Recurring Bill
                  </Label>
                </div>

                {formData.recurring && (
                  <div key="frequency-selector">
                    <Label className="dark:text-gray-300">Frequency</Label>
                    <Select 
                      value={formData.frequency} 
                      onValueChange={(value) => {
                        setFormData({ ...formData, frequency: value });
                      }}
                    >
                      <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600" data-testid="frequency-select">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent container={document.body} className="dark:bg-gray-800 dark:border-gray-700">
                        <SelectItem value="weekly" className="dark:text-gray-100 dark:hover:bg-gray-700">Weekly</SelectItem>
                        <SelectItem value="monthly" className="dark:text-gray-100 dark:hover:bg-gray-700">Monthly</SelectItem>
                        <SelectItem value="yearly" className="dark:text-gray-100 dark:hover:bg-gray-700">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-rose-600 hover:bg-rose-700"
                  data-testid="submit-bill"
                >
                  {loading ? (editMode ? 'Updating...' : 'Adding...') : (editMode ? 'Update Bill' : 'Add Bill')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bills-grid">
          {bills.length > 0 ? (
            bills.map((bill) => {
              const upcoming = isUpcoming(bill.due_date);
              return (
                <Card key={bill.id} className={`glass p-6 card-hover dark:bg-gray-800 ${upcoming ? 'border-2 border-rose-400' : ''}`} data-testid={`bill-item-${bill.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(bill)}
                        className="text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                        data-testid={`edit-bill-${bill.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(bill.id)}
                        className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                        data-testid={`delete-bill-${bill.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {upcoming && (
                    <div className="flex items-center space-x-2 mb-2 text-rose-600 dark:text-rose-400">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Due Soon!</span>
                    </div>
                  )}
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">{bill.name}</h3>
                  <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 mb-3">{formatCurrency(bill.amount, user?.currency)}</p>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Due: {new Date(bill.due_date).toLocaleDateString()}</p>
                    {bill.recurring && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">Repeats {bill.frequency}</p>
                    )}
                  </div>
                </Card>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12" data-testid="no-bills">
              <p className="text-gray-500 dark:text-gray-400">No bills yet. Add your first bill!</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}